import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { array, number, object, ObjectSchema, string } from 'yup'
import { ChartBaseFormValues, chartBaseValidationSchema } from './base'
import { CandidateFullName } from './chart-general-form'

export type ChartRaceFormValues = {
  id?: string
  name: string
  type: string
  candidates: number
  candidateNames: CandidateFullName[]
  writeIns: number
  voteFor: number
}

export type NonPartisanChartFormValues = ChartBaseFormValues & {
  races: ChartRaceFormValues[]
}

const chartValidationSchema: ObjectSchema<NonPartisanChartFormValues> = object()
  .shape({
    ...chartBaseValidationSchema,
    races: array()
      .of(
        object().shape(
          {
            id: string(),
            name: string().required(),
            type: string().required(),
            writeIns: number()
              .min(0)
              .default(1)
              .required()
              .when(['type', 'candidates'], ([type, candidates], schema) => {
                return type === 'Race' && candidates === 0
                  ? schema.min(
                      1,
                      'Race must have either candidates or write-ins'
                    )
                  : schema
              }),
            voteFor: number()
              .required()
              .when('type', {
                is: 'Race',
                then: (schema) => schema.min(1).required(),
                otherwise: (schema) => schema.optional(),
              }),
            candidates: number()
              .min(0)
              .default(0)
              .required('candidates is required')
              .when(['type', 'writeIns'], ([type, writeIns], schema) => {
                return type === 'Race' && writeIns === 0
                  ? schema.min(
                      1,
                      'Race must have either candidates or write-ins'
                    )
                  : schema
              }),
            candidateNames: array()
              .of(
                object({
                  name: string().required(),
                  order: number().required(),
                })
              )
              .default([])
              .required(),
          },
          [
            ['candidates', 'writeIns'],
            ['type', 'candidates'],
            ['writeIns', 'type'],
          ]
        )
      )
      .required()
      .test({
        message: 'Chart must have at least one race',
        test: (arr) => {
          if (!arr) {
            return false
          }
          return arr!.length > 0
        },
      }),
  })
  .required()

export function useChartForm() {
  return useForm<NonPartisanChartFormValues>({
    resolver: yupResolver(chartValidationSchema),
    reValidateMode: 'onChange',
    defaultValues: {
      municipality: '',
      county: '',
      precinct: '',
      electionDate: '',
      showCandidateNames: false,
      races: [],
    },
  })
}
